import { useGetTutorialVideo } from '@/utils/api';
import { useIsDirectEntry } from '@/utils/user';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { TagPill } from '@repo/ui';
import React, { useMemo, useState } from 'react';

interface TutorialDialogProps {
  open: boolean;
  onClose: () => void;
  showRange?: number[],
  initialIndex?: number,
}

interface VideoMeta {
  title: string,
  description: string,
  videoId: string
}



const FOUNDATION_VIDEOS: VideoMeta[] = [
  {
    title: "🔮 Magic Setup",
    description: "✨ Use Magic Setup to upload your transcript and chat with our AI assistant to complete your profile.\n🛠️ Or, manually edit your profile anytime.",
    videoId: "Slide 1"
  },
  {
    title: "📄 Getting to Know You",
    description: "📝 Answer a few questions so Zube can give you personalised guidance.\n↩️ You can always come back and update your answers later.",
    videoId: "Slide 2"
  },
  {
    title: "🎯 AI Recommendations",
    description: "🔍 Click to see your personalised recommendations, or input a query to get ask for a more specific recommendation.",
    videoId: "Slide 5"
  },
  {
    title: "⭐ Browse Courses",
    description: "👀 Browse courses and add your favorites to your watchlist.",
    videoId: "Browse Courses"
  },
  {
    title: "🎓 Course Watchlist",
    description: "👉 View your watchlist of courses, and see which of them need a personal statement.",
    videoId: "Watchlist"
  },
  {
    title: "🎓 Your Pathway to Undergraduate",
    description: "🛤️ Understand which Foundation courses lead to which degrees, and see if your high school grades allow you to study for them.",
    videoId: "Course Pathway Feature"
  },
  {
    title: "⚠️ Oxford, Cambridge, Imperial College, and LSE don't offer Foundation courses",
    description: "🎯 If you're applying to these universities, we have some suggested pathways where previous students have been accepted from.",
    videoId: "Oxbridge_ICL_LSE Exceptions"
  },
  {
    title: "✍️ AI-Powered Assistance",
    description: "📝 When you've picked your top 5 courses, it's time to write your Personal Statement.\n🤖 Zube will craft a custom outline for you based on your profile.\n✅ Submit drafts anytime for AI-powered feedback — and keep improving until you see \"Good to Go!\"",
    videoId: "AI Personal Statement"
  },
  {
    title: "🏅 Scholarships",
    description: "💸 Get tailored scholarship suggestions that match your profile and ambitions.",
    videoId: "Slide 7"
  }
]

const UCAS_VIDEOS: VideoMeta[] = [
  {
    title: "🔮 Magic Setup",
    description: "✨ Use Magic Setup to upload your transcript and chat with our AI assistant to complete your profile.\n🛠️ Or, manually edit your profile anytime.",
    videoId: "Slide 1"
  },
  {
    title: "📄 Getting to Know You",
    description: "📝 Answer a few questions so Zube can give you personalised guidance.\n↩️ You can always come back and update your answers later.",
    videoId: "Slide 2"
  },
  {
    title: "🎯 AI Recommendations",
    description: "🔍 Find at least 1 course that interests you and add it to your watchlist to unlock personalised recommendations.",
    videoId: "Slide 5"
  },
  {
    title: "⭐ Browse Courses",
    description: "👀 Browse courses and add your favorites to your watchlist.",
    videoId: "Slide 3"
  },
  {
    title: "🎓 Course Watchlist and UCAS",
    description: "👉 Once you've picked your top 5, drag them into the UCAS section to begin your Personal Statement.",
    videoId: "Slide 4"
  },
  {
    title: "✍️ AI-Powered Assistance",
    description: "📝 When you've picked your top 5 courses, it's time to write your Personal Statement.\n🤖 Zube will craft a custom outline for you based on your profile.\n✅ Submit drafts anytime for AI-powered feedback — and keep improving until you see “Good to Go!”",
    videoId: "Slide 6"
  },
  {
    title: "🏅 Scholarships",
    description: "💸 Get tailored scholarship suggestions that match your profile and ambitions.",
    videoId: "Slide 7"
  }
]


const TutorialDialog: React.FC<TutorialDialogProps> = ({
  open,
  onClose,
  showRange = [],
  initialIndex = 0
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const isDirectEntry = useIsDirectEntry();

  const tutorials = useMemo(() => {
    const vids = isDirectEntry ? UCAS_VIDEOS : FOUNDATION_VIDEOS;
    return vids.slice(showRange[0] ?? 0, showRange[1] ? showRange[1] + 1 : vids.length);
  }, [showRange]);

  const videoId = tutorials[currentIndex]?.videoId || '';

  const { data: tutorialData, isLoading, error } = useGetTutorialVideo(videoId, { enabled: open });

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : tutorials.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < tutorials.length - 1 ? prev + 1 : 0));
  };

  const handleTitleClick = (index: number) => {
    setCurrentIndex(index);
  };

  const currentTutorial = tutorials[currentIndex];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Tutorial: {currentTutorial?.title}
          </Typography>
          {tutorials.length > 1 && <Stack flexShrink={0} direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={handlePrevious}>
              <CaretLeftIcon />
            </IconButton>
            <Typography>
              {currentIndex + 1} of {tutorials.length}
            </Typography>
            <IconButton onClick={handleNext}>
              <CaretRightIcon />
            </IconButton>
          </Stack>}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', width: '100%' }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error">
              Failed to load tutorial video. Please try again.
            </Alert>
          )}

          {tutorialData?.success && tutorialData.data.videoUrl && (
            <Box
              component="video"
              src={tutorialData.data.videoUrl}
              controls
              autoPlay
              loop
              muted
              sx={{
                width: '100%',
                maxHeight: '400px',
                borderRadius: 2,
                backgroundColor: 'grey.100'
              }}
            />
          )}
        </Box>
        <Typography variant="body1" color="text.secondary">
          {currentTutorial?.description}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width='100%' spacing={2}>
          {tutorials.length > 1 ? <Stack
            direction="row"
            spacing={1}
            sx={{
              overflowX: 'auto',
              pb: 1,
              '&::-webkit-scrollbar': {
                height: 6,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'grey.100',
                borderRadius: 3,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'grey.400',
                borderRadius: 3,
              },
            }}
          >
            {tutorials.map((tutorial, index) => (
              <TagPill
                key={index}
                onClick={() => handleTitleClick(index)}
                color={index === currentIndex ? 'orange' : 'blue'}
                text={tutorial.title}
                emphasize={index === currentIndex}
              />
            ))}
          </Stack> : <Box flex={1} />}
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog >
  );
};

export default TutorialDialog;
